import { useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { layoutForPath } from '../utils/layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, Trash2, FileText, CheckSquare, Eye, Search, ChevronLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIVISION_META = {
  shop: { label: 'Shop', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  field: { label: 'Field', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  both: { label: 'Shop + Field', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

function scopeForPath(pathname) {
  if (pathname.startsWith('/field')) return 'field';
  if (pathname.startsWith('/admin')) return 'all';
  return 'shop';
}

const SCOPE_COPY = {
  shop: { title: 'Shop Forms', blurb: 'Fillable PDF templates technicians complete inside shop jobs' },
  field: { title: 'Field Forms', blurb: 'Fillable PDF templates for field crews' },
  all: { title: 'Form Templates', blurb: 'Every form across the Shop and Field divisions' },
};

function TemplatePreview({ template, onClose }) {
  const [page, setPage] = useState(0);
  const [pageUrl, setPageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [openingPdf, setOpeningPdf] = useState(false);
  const pageCount = template.page_count || 1;

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    axios.get(`${API}/pdf-templates/${template.id}/page/${page}`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPageUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) { setPageUrl(null); setFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [template.id, page]);

  const openPdf = async () => {
    setOpeningPdf(true);
    try {
      const res = await axios.get(`${API}/pdf-templates/${template.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${template.name}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Could not open the PDF');
    } finally {
      setOpeningPdf(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="template-preview-dialog">
        <DialogHeader>
          <DialogTitle className="pr-8">{template.name}</DialogTitle>
          <DialogDescription>
            {pageCount} page{pageCount !== 1 ? 's' : ''} · read-only preview of the uploaded template
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="preview-prev-page">
              <ChevronLeft size={14} />
            </Button>
            <span className="text-sm font-medium text-slate-700" data-testid="preview-page-label">
              Page {page + 1} of {pageCount}
            </span>
            <Button size="sm" variant="outline" disabled={page >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} data-testid="preview-next-page">
              <ChevronRight size={14} />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <CheckSquare size={12} /> {template.fields?.length || 0} fields
            </span>
            <Button size="sm" variant="outline" onClick={openPdf} disabled={openingPdf} data-testid="preview-open-pdf">
              {openingPdf ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ExternalLink size={14} className="mr-1.5" />}
              Open PDF
            </Button>
          </div>
        </div>
        <div className="bg-slate-100 p-3 rounded-md min-h-[420px] flex items-center justify-center">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={16} className="animate-spin" /> Rendering page {page + 1}…
            </div>
          ) : failed || !pageUrl ? (
            <div className="text-center text-sm text-slate-500" data-testid="preview-page-error">
              This page couldn't be rendered. Use <strong>Open PDF</strong> to view the original file.
            </div>
          ) : (
            <img src={pageUrl} alt={`${template.name} page ${page + 1}`}
              className="w-full h-auto rounded border border-slate-300 bg-white shadow-sm"
              data-testid="preview-page-image" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FormTemplates() {
  const { pathname } = useLocation();
  const scope = scopeForPath(pathname);
  const [templates, setTemplates] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadDivision, setUploadDivision] = useState(scope === 'field' ? 'field' : 'shop');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [search, setSearch] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const { user } = useAuth();
  const Layout = layoutForPath(pathname);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pdf-templates`, {
        params: scope === 'all' ? {} : { division: scope },
      });
      setTemplates(res.data);
    } catch {
      toast.error('Failed to load templates');
    }
  }, [scope]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { setUploadDivision(scope === 'field' ? 'field' : 'shop'); }, [scope]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadName.trim()) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName.trim());
      formData.append('division', uploadDivision);
      await axios.post(`${API}/pdf-templates`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Template uploaded successfully');
      setShowUpload(false);
      setUploadName('');
      setUploadFile(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (template) => {
    try {
      await axios.delete(`${API}/pdf-templates/${template.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
    setDeleteConfirm(null);
  };

  const changeDivision = async (template, division) => {
    try {
      await axios.patch(`${API}/pdf-templates/${template.id}`, { division });
      toast.success(`Moved to ${DIVISION_META[division].label}`);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move template');
    }
  };

  const canEdit = user?.role === 'management' || (user?.effective_permissions || []).includes('form-templates.edit');
  const perms = user?.effective_permissions || [];
  const canView = canEdit || perms.includes('form-templates') || (scope === 'field' && perms.includes('field.calendar'));

  if (!canView) {
    return (
      <Layout>
        <div className="text-center p-12">
          <p className="text-slate-600">You don't have permission to view form templates</p>
        </div>
      </Layout>
    );
  }

  const q = search.trim().toLowerCase();
  const visible = templates.filter((t) => {
    const division = t.division || 'shop';
    if (scope === 'all' && divisionFilter !== 'all') {
      if (divisionFilter === 'both' ? division !== 'both' : !(division === divisionFilter || division === 'both')) return false;
    }
    return !q || (t.name || '').toLowerCase().includes(q);
  });

  const copy = SCOPE_COPY[scope];

  return (
    <Layout title={copy.title}>
      <div data-testid="form-templates-page" className="space-y-6">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">{copy.title}</h3>
            <p className="text-slate-600">{copy.blurb}</p>
          </div>
          {canEdit && (
            <Dialog open={showUpload} onOpenChange={setShowUpload}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="upload-template-btn">
                  <Upload size={20} className="mr-2" />
                  Upload Template
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload PDF Template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4" data-testid="upload-template-form">
                  <div className="space-y-2">
                    <Label htmlFor="template-name">Template Name</Label>
                    <Input
                      id="template-name"
                      value={uploadName}
                      onChange={(e) => setUploadName(e.target.value)}
                      placeholder="e.g. Inspection Checklist"
                      required
                      data-testid="template-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Division</Label>
                    <Select value={uploadDivision} onValueChange={setUploadDivision}>
                      <SelectTrigger data-testid="template-division-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shop">Shop</SelectItem>
                        <SelectItem value="field">Field</SelectItem>
                        <SelectItem value="both">Shop + Field</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Decides which division's Forms page lists it. Admin always sees everything.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="template-file">PDF File</Label>
                    <Input
                      id="template-file"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      required
                      data-testid="template-file-input"
                    />
                    <p className="text-xs text-slate-500">Upload a fillable PDF with form fields. Fields will be extracted automatically.</p>
                  </div>
                  <Button type="submit" className="w-full" disabled={uploading} data-testid="submit-upload-btn">
                    {uploading ? 'Uploading...' : 'Upload Template'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search forms by name"
              className="pl-9"
              data-testid="template-search"
            />
          </div>
          {scope === 'all' && (
            <Select value={divisionFilter} onValueChange={setDivisionFilter}>
              <SelectTrigger className="w-44" data-testid="template-division-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All divisions</SelectItem>
                <SelectItem value="shop">Shop forms</SelectItem>
                <SelectItem value="field">Field forms</SelectItem>
                <SelectItem value="both">Shared (both)</SelectItem>
              </SelectContent>
            </Select>
          )}
          <span className="text-xs text-slate-500" data-testid="template-count">
            {visible.length} of {templates.length} form{templates.length !== 1 ? 's' : ''}
          </span>
        </div>

        {visible.length === 0 ? (
          <Card className="p-12 text-center bg-white border border-slate-200" data-testid="form-templates-empty">
            <FileText size={48} className="mx-auto mb-4 text-slate-300" />
            {templates.length === 0 ? (
              <>
                <p className="text-slate-600 mb-2">No form templates yet</p>
                <p className="text-sm text-slate-400">
                  {canEdit ? 'Upload a fillable PDF to get started' : 'Nothing has been uploaded for this division yet'}
                </p>
              </>
            ) : (
              <p className="text-slate-600">No forms match your search or filter</p>
            )}
          </Card>
        ) : (
          <div className="grid gap-4">
            {visible.map((t) => {
              const division = t.division || 'shop';
              const meta = DIVISION_META[division] || DIVISION_META.shop;
              return (
                <Card key={t.id} className="p-5 bg-white border border-slate-200 shadow-sm" data-testid={`template-card-${t.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="bg-slate-100 p-3 rounded-md shrink-0">
                        <FileText size={24} className="text-slate-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-semibold text-slate-900 truncate">{t.name}</h4>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}
                            data-testid={`template-division-badge-${t.id}`}>
                            {meta.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <CheckSquare size={12} />
                            {t.fields?.length || 0} form field{(t.fields?.length || 0) !== 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-slate-400">
                            {t.page_count || 1} page{(t.page_count || 1) !== 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-slate-400">
                            Uploaded {new Date(t.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewing(t)}
                        data-testid={`preview-template-${t.id}`}
                      >
                        <Eye size={14} className="mr-1" /> Preview
                      </Button>
                      {canEdit && (
                        <>
                          <Select value={division} onValueChange={(v) => changeDivision(t, v)}>
                            <SelectTrigger className="w-32 h-9" data-testid={`template-division-change-${t.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="shop">Shop</SelectItem>
                              <SelectItem value="field">Field</SelectItem>
                              <SelectItem value="both">Shop + Field</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirm(t)}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            data-testid={`delete-template-${t.id}`}
                          >
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {previewing && <TemplatePreview template={previewing} onClose={() => setPreviewing(null)} />}

      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This will remove the template from all jobs and delete any submissions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-template">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-template"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
